<template>
  <v-row>
    <v-col cols="12">
      <v-card class="gl-card" flat>
        <!-- HEADER BAND -->
        <div class="gl-header pl-base-bg">
          <div class="gl-header-icon">
            <v-icon color="#fff" size="22">mdi-book-search-outline</v-icon>
          </div>
          <div>
            <div class="gl-title">General Ledger</div>
            <div class="gl-subtitle">Every entry hitting one account, over a date range</div>
          </div>
        </div>

        <div class="pa-6">
          <v-alert type="info" outlined dense border="left" class="gl-disclaimer">
            Drill-down of individual Journal Entry lines against a single
            account — the Trial Balance shows the netted summary; this
            shows every line behind it.
          </v-alert>

          <!-- FILTERS -->
          <div class="gl-filter-bar">
            <v-row dense align="center">
              <v-col cols="12" md="4">
                <v-select
                  v-model="accountId"
                  :items="accounts"
                  item-text="accountLabel"
                  item-value="_id"
                  label="Account"
                  outlined
                  dense
                  hide-details
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedFrom"
                      label="From"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="from" @input="fromMenu = false" />
                </v-menu>
              </v-col>

              <v-col cols="12" md="3">
                <v-menu v-model="toMenu" :close-on-content-click="false" offset-y max-width="290" min-width="290">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formattedTo"
                      label="To"
                      readonly
                      outlined
                      dense
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker v-model="to" @input="toMenu = false" />
                </v-menu>
              </v-col>

              <v-col cols="12" md="2" class="gl-actions">
                <v-btn class="gl-apply-btn" :disabled="!accountId" :loading="loading" @click="loadGeneralLedger">
                  Apply
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <template v-if="generalLedger">
            <v-row dense class="mb-6">
              <v-col cols="6" md="4">
                <v-card class="gl-kpi-card gl-kpi-card--value pa-4" flat>
                  <div class="gl-kpi-label">{{ generalLedger.account.code }} {{ generalLedger.account.name }}</div>
                  <div class="gl-kpi-value">{{ generalLedger.account.type }}</div>
                </v-card>
              </v-col>
              <v-col cols="6" md="4">
                <v-card class="gl-kpi-card gl-kpi-card--tax pa-4" flat>
                  <div class="gl-kpi-label">Opening Balance</div>
                  <div class="gl-kpi-value">{{ formatBalance(generalLedger.openingBalance) }}</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card class="gl-kpi-card gl-kpi-card--total pa-4" flat>
                  <div class="gl-kpi-label">Closing Balance</div>
                  <div class="gl-kpi-value">{{ formatBalance(generalLedger.closingBalance) }}</div>
                </v-card>
              </v-col>
            </v-row>

            <app-ag-grid
              title="Ledger Lines"
              :columnDefs="columns"
              :rowData="generalLedger.lines"
              :newBtn="false"
              :refreshBtn="false"
              :mobileCard="lineMobileCard"
              height="height: 480px"
            />
          </template>

          <div v-else class="gl-empty-state">
            Select an account and date range, then click Apply.
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";

export default {
  name: "GeneralLedgerPage",

  components: { AppAgGrid },

  data() {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      loading: false,
      accountId: null,
      from: monthStart.toISOString().slice(0, 10),
      to: today.toISOString().slice(0, 10),
      fromMenu: false,
      toMenu: false,

      columns: [
        { headerName: "Date", field: "date", valueFormatter: (p) => this.formatDate(p.value), minWidth: 110 },
        { headerName: "Entry #", field: "entryNumber", minWidth: 120 },
        { headerName: "Narration", field: "narration", minWidth: 200 },
        { headerName: "Source", field: "source", minWidth: 140 },
        { headerName: "Debit", field: "debit", valueFormatter: (p) => this.formatINR(p.value), minWidth: 120 },
        { headerName: "Credit", field: "credit", valueFormatter: (p) => this.formatINR(p.value), minWidth: 120 },
        { headerName: "Running Balance", field: "runningBalance", valueFormatter: (p) => this.formatBalance(p.value), minWidth: 150 },
      ],
    };
  },

  computed: {
    generalLedger() {
      return this.$store.state.ledgerReports.generalLedger;
    },

    accounts() {
      return this.$store.state.accounts.accounts.map((a) => ({
        ...a,
        accountLabel: `${a.code} — ${a.name}`,
      }));
    },

    formattedFrom() {
      return this.formatDate(this.from);
    },

    formattedTo() {
      return this.formatDate(this.to);
    },

    lineMobileCard() {
      return {
        title: (row) => row.narration || row.source,
        subtitle: (row) => this.formatDate(row.date),
        fields: (row) => [
          { label: "Debit", value: this.formatINR(row.debit) },
          { label: "Credit", value: this.formatINR(row.credit) },
          { label: "Balance", value: this.formatBalance(row.runningBalance) },
        ],
      };
    },
  },

  created() {
    this.$store.dispatch("accounts/getAccounts");
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    // The API's debit/credit running balance is always debit-minus-credit,
    // so a credit-normal account (INCOME/LIABILITY/EQUITY) with real
    // activity comes back negative — mathematically correct, just shown
    // as its absolute value here since the sign is an accounting
    // convention, not something to surface directly in the UI.
    formatBalance(value) {
      if (value === null || value === undefined) return "0";
      return this.formatINR(Math.abs(value));
    },

    formatDate(value) {
      if (!value) return "-";
      const d = new Date(value);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    async loadGeneralLedger() {
      if (!this.accountId) return;
      this.loading = true;
      try {
        await this.$store.dispatch("ledgerReports/getGeneralLedger", {
          accountId: this.accountId,
          from: this.from,
          to: this.to,
        });
      } catch (err) {
        console.error(err);
        this.$toast?.error(err?.response?.data?.message || "Failed to load General Ledger");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.gl-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.gl-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.gl-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gl-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.gl-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.gl-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.gl-filter-bar {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 16px 16px 4px;
  margin-bottom: 24px;
}

.gl-actions {
  display: flex;
  align-items: center;
}

.gl-apply-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.gl-kpi-card {
  border-radius: 0;
  color: #fff;
  box-shadow: 0 6px 16px rgba(15, 30, 50, 0.12);
}

.gl-kpi-card--value {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}
.gl-kpi-card--tax {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}
.gl-kpi-card--total {
  background: linear-gradient(135deg, #0d8478 0%, #15a355 100%);
}

.gl-kpi-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.gl-kpi-value {
  font-size: 18px;
  font-weight: 700;
  margin-top: 6px;
  letter-spacing: -0.01em;
}

.gl-empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 14px;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
